import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { ISearchOpenFileInfo } from "../../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { RootState } from "../../../src/taskpane/redux";
import { InfoPanelMode } from "../../Panels/CMFileInfoPanel";
import { gridSelectedItems } from "../ContentManagerBody";
import { GridProps } from "../useContentManagerBody";
import { useMenuItem_Automation } from "./useMenuItem_Automation";
import { useMenuItem_Checkin } from "./useMenuItem_Checkin";
import { useMenuItem_Checkout } from "./useMenuItem_Checkout";
import { useMenuItem_ClearSearch } from "./useMenuItem_ClearSearch";
import { useMenuItem_CopyTo } from "./useMenuItem_CopyTo";
import { useMenuItem_CurrentLocation } from "./useMenuItem_CurrentLocation";
import { useMenuItem_Delete } from "./useMenuItem_Delete";
import { useMenuItem_CancelAssignment } from "./useMenuItem_CancelAssignment";
import { useMenuItem_DiscardCheckout } from "./useMenuItem_DiscardCheckout";
import { useMenuItem_Download } from "./useMenuItem_Download";
import { useMenuItem_DownloadAsPDF } from "./useMenuItem_DownloadAsPDF";
import { useMenuItem_Error } from "./useMenuItem_Error";
import { useMenuItem_Filter } from "./useMenuItem_Filter";
import { useMenuItem_LoadingFiles } from "./useMenuItem_LoadingFiles";
import { useMenuItem_More } from "./useMenuItem_More";
import { useMenuItem_MoveTo } from "./useMenuItem_MoveTo";
import { useMenuItem_MoveToCancel } from "./useMenuItem_MoveToCancel";
import { useMenuItem_New } from "./useMenuItem_New";
import { useMenuItem_Open } from "./useMenuItem_Open";
import { useMenuItem_PII } from "./useMenuItem_PII";
import { useMenuItem_Pin } from "./useMenuItem_Pin";
import { useMenuItem_Preview } from "./useMenuItem_Preview";
import { useMenuItem_Properties } from "./useMenuItem_Properties";
import { useMenuItem_Publish } from "./useMenuItem_Publish";
import { useMenuItem_Refresh } from "./useMenuItem_Refresh";
import { useMenuItem_Rename } from "./useMenuItem_Rename";
import { useMenuItem_Replace } from "./useMenuItem_Replace";
import { useMenuItem_SavingFiles } from "./useMenuItem_SavingFiles";
import { useMenuItem_SelectedFileCount } from "./useMenuItem_SelectedFileCount";
import { useMenuItem_SelectedMoveFileCount } from "./useMenuItem_SelectedMoveFileCount";
import { useMenuItem_Share } from "./useMenuItem_Share";
import { useMenuItem_Unpublish } from "./useMenuItem_Unpublish";
import { useMenuItem_Upload } from "./useMenuItem_Upload";
import { useMenuItem_VersionAppRej } from "./useMenuItem_VersionAppRej";
import { useMenuItem_VersionHistory } from "./useMenuItem_VersionHistory";
import { useMenuItem_Views } from "./useMenuItem_Views";
import { useMenuItem_ClearFilter } from "./useMenuItem_ClearFilter";
import { useMenuItem_SaveAsPDF } from "./useMenuItem_SaveAsPDF";
import { useMenuItem_SigningRequest } from "./useMenuItem_SigningRequest";
import { useMenuItem_ShareToTeams } from "./useMenuItem_ShareToTeams";
import { ModeEnum } from "../../../src/taskpane/components/enums/modeEnum";
import { useMenuItem_NewDatasourceDocumentTemplate } from "./useMenuItem_NewDatasourceFileTemplate";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { SharePanelMode } from "../../Panels/CMFileSharePanel";
import { useMenuItem_CopyLink } from "./useMenuItem_CopyLink";

export interface ICommandBarMenuProps {
    items: IContextualMenuItem[];
    farItems: IContextualMenuItem[];
}

export function useMenuItems(
    mode: ModeEnum,
    selectedItems: any[],
    waitFor: boolean,
    setWaitFor: (waitFor: boolean) => void,
    waitForContext: string,
    setWaitForContext: (waitForContext: string) => void,
    isInOneDrive: boolean,
    listFields: any,
    selectedItemInfo?: ISearchOpenFileInfo,
    setIsCMCreateFolderPanelOpen?: (isCMCreateFolderPanelOpen: boolean) => void,
    setIsCMUploadFilesPanelOpen?: (isCMUploadFilesPanelOpen: boolean) => void,
    setShowRenameDialog?: (showRenameDialog: boolean) => void,
    setIsCMFileInfoPanelOpen?: (isCMFileInfoPanelOpen: boolean) => void,
    setInfoPanelMode?: (infoPanelMode: InfoPanelMode) => void,
    setApprovalFiles?: (approvalFiles: any[]) => void,
    setIsApprovalsPanelOpen?: (isApprovalsPanelOpen: boolean) => void,
    setShowDeleteDialog?: (showDeleteDialog: boolean) => void,
    setShowVersionHistoryDialog?: (showVersionHistoryDialog) => void,
    setIsPublish?: (isPublish: boolean) => void,
    setShowCheckinDialog?: (showCheckinDialog: boolean) => void,
    gridProps?: GridProps,
    setGridProps?: (gridProps: GridProps) => void,
    setSelectedItems?: (selectedItems: gridSelectedItems) => void,
    setIsCMFilePreviewPanelOpen?: (isCMFilePreviewPanelOpen: boolean) => void,
    setSelectionCleared?: (selectionCleared: boolean) => void,
    setGridItemIsBusy?: (items: any[], isBusy: boolean) => void,
    updateGridItems?: (items: any[]) => void,
    viewsWithColumns?: IViewsDropdown[],
    selectedView?: IViewsDropdown,
    setSelectedView?: (view: IViewsDropdown) => void,
    setIsCMErrorModalOpen?: (isCMErrorModalOpen: boolean) => void,
    setErrorMessage?: (errorMessage: string) => void,
    setIsCMVersionAppRejModalOpen?: (isCMVersionAppRejModalOpen: boolean) => void,
    setIsCMFileFilterPanelOpen?: (isCMFileFilterPanelOpen: boolean) => void,
    newDocumentTemplates?: NewDocumentTemplate[],
    openCreateTemplateModal?: (selectedTemplate: NewDocumentTemplate) => void,
    quickFilterValue?: string,
    menuItem_QuickFilter?: IContextualMenuItem,
    setIsCMMoveFilesListPanelOpen?: (isCMMoveFilesListPanelOpen: boolean) => void,
    setShowConfirmMoveToDialog?: (isShowConfirmMoveToDialog: boolean) => void,
    isFetchingData?: boolean,
    openSharePanel?: (mode: SharePanelMode) => void,
    setShowCancelAssignmentDialog?: (showCancelAssignmentDialog: boolean) => void,
    libraryMinVerEnabled?: boolean,
    openDuplicateFileVersionSelectorModal?: (isDuplicate: boolean, showVersion: boolean) => void,
    reloadGrid?: (gridData?: GridProps) => void,
    pickFiles?: () => void,
    pickFolder?: () => void,
    setIsSigningPanelOpen?: (isSigningPanelOpen: boolean) => void,
    setIsFileUploadProgressPanelOpen?: (isFileUploadProgressPanelOpen: boolean) => void,
) {
    const { menuItem_Automation } = useMenuItem_Automation(selectedItems, waitFor, setApprovalFiles, setIsApprovalsPanelOpen);
    const { menuItem_Checkin } = useMenuItem_Checkin(selectedItems, waitFor, setWaitFor, setIsPublish, setShowCheckinDialog, isInOneDrive);
    const { menuItem_Checkout } = useMenuItem_Checkout(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_ClearSearch } = useMenuItem_ClearSearch(selectedItems, waitFor);
    const { menuItem_ClearFilter } = useMenuItem_ClearFilter(waitFor);
    const { menuItem_CopyTo } = useMenuItem_CopyTo(gridProps?.items?.length, selectedItems, setShowConfirmMoveToDialog, isInOneDrive, isFetchingData, setIsCMUploadFilesPanelOpen);
    const { menuItem_CurrentLocation } = useMenuItem_CurrentLocation(selectedItems, isInOneDrive);
    const { menuItem_Delete, deleteSuccess } = useMenuItem_Delete(selectedItems, waitFor, setWaitFor, setShowDeleteDialog, gridProps, setGridProps, setSelectionCleared);
    const { menuItem_CancelAssignment } = useMenuItem_CancelAssignment(selectedItems, waitFor, setShowCancelAssignmentDialog);
    const { menuItem_DiscardCheckout } = useMenuItem_DiscardCheckout(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Download } = useMenuItem_Download(listFields, selectedItems, waitFor, setWaitFor, isInOneDrive, setWaitForContext);
    const { menuItem_DownloadAsPDF } = useMenuItem_DownloadAsPDF(listFields, selectedItems, waitFor, setWaitFor, isInOneDrive, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Error } = useMenuItem_Error(setIsCMUploadFilesPanelOpen, setIsCMMoveFilesListPanelOpen);
    const { menuItem_Filter } = useMenuItem_Filter(setIsCMFileFilterPanelOpen);
    const { menuItem_LoadingFiles } = useMenuItem_LoadingFiles(selectedItems, waitFor, waitForContext);
    const { menuItem_MoveTo } = useMenuItem_MoveTo(gridProps?.items?.length, selectedItems, setShowConfirmMoveToDialog, isInOneDrive, isFetchingData);
    const { menuItem_MoveToCancel } = useMenuItem_MoveToCancel(selectedItems, waitFor);
    const { menuItem_New } = useMenuItem_New(selectedItems, waitFor, setIsCMCreateFolderPanelOpen, newDocumentTemplates, openCreateTemplateModal);
    const { menuItem_Open } = useMenuItem_Open(selectedItems, waitFor, setWaitFor, isInOneDrive, selectedItemInfo);
    const { menuItem_PII } = useMenuItem_PII(selectedItems, waitFor, gridProps, setGridProps, setSelectedItems);
    const { menuItem_Pin } = useMenuItem_Pin(selectedItems, waitFor, isInOneDrive, selectedItemInfo);
    const { menuItem_Preview, menuItem_Preview_IconOnly } = useMenuItem_Preview(selectedItems, setIsCMFilePreviewPanelOpen);
    const { menuItem_Properties, menuItem_Properties_IconOnly } = useMenuItem_Properties(selectedItems, setIsCMFileInfoPanelOpen, setInfoPanelMode);
    const { menuItem_Publish } = useMenuItem_Publish(selectedItems, waitFor, setWaitFor, setIsPublish, setShowCheckinDialog, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Refresh } = useMenuItem_Refresh(selectedItems);
    const { menuItem_Rename } = useMenuItem_Rename(selectedItems, waitFor, setShowRenameDialog);
    const { menuItem_Replace } = useMenuItem_Replace(selectedItems, waitFor, setIsCMFileInfoPanelOpen, setInfoPanelMode);
    const { menuItem_SaveAsPDF } = useMenuItem_SaveAsPDF(selectedItems, waitFor, setWaitFor, isInOneDrive, setIsCMErrorModalOpen, setErrorMessage, openDuplicateFileVersionSelectorModal, reloadGrid);
    const { menuItem_SavingFiles } = useMenuItem_SavingFiles(setIsCMMoveFilesListPanelOpen, setIsFileUploadProgressPanelOpen);
    const { menuItem_SelectedFileCount } = useMenuItem_SelectedFileCount(selectedItems, waitFor, setSelectionCleared);
    const { menuItem_SelectedMoveFileCount } = useMenuItem_SelectedMoveFileCount(setIsCMMoveFilesListPanelOpen, isInOneDrive);
    const { menuItem_Share } = useMenuItem_Share(selectedItems, openSharePanel);
    const { menuItem_CopyLink } = useMenuItem_CopyLink(selectedItems, openSharePanel);
    const { menuItem_SigningRequest } = useMenuItem_SigningRequest(selectedItems, waitFor, setApprovalFiles, setIsSigningPanelOpen);
    const { menuItem_Unpublish } = useMenuItem_Unpublish(selectedItems, waitFor, setWaitFor, isInOneDrive, setGridItemIsBusy, updateGridItems, setIsCMErrorModalOpen, setErrorMessage);
    const { menuItem_Upload } = useMenuItem_Upload(selectedItems, waitFor, newDocumentTemplates, pickFiles, pickFolder, setIsCMUploadFilesPanelOpen);
    const { menuItem_VersionAppRej } = useMenuItem_VersionAppRej(selectedItems, waitFor, setIsCMVersionAppRejModalOpen, isInOneDrive);
    const { menuItem_VersionHistory } = useMenuItem_VersionHistory(selectedItems, waitFor, setShowVersionHistoryDialog, isInOneDrive);
    const { menuItem_Views } = useMenuItem_Views(viewsWithColumns, selectedView, setSelectedView, quickFilterValue, libraryMinVerEnabled);
    const { menuItem_More } = useMenuItem_More(selectedItems, isInOneDrive, menuItem_Checkout, menuItem_Checkin, menuItem_DiscardCheckout, menuItem_Publish, menuItem_Unpublish, menuItem_VersionAppRej);
    const { menuItem_ShareToTeams } = useMenuItem_ShareToTeams(selectedItems, isInOneDrive, setWaitFor, setWaitForContext, mode);
    const { menuItem_NewDatasourceDocumentTemplate } = useMenuItem_NewDatasourceDocumentTemplate(selectedItems, waitFor, newDocumentTemplates, openCreateTemplateModal);

    const [contextMenuState, _setContextMenuState] = React.useState<IContextualMenuItem[]>();

    const contextMenuRef = React.useRef(contextMenuState);
    const setContextMenu = (data: IContextualMenuItem[]) => {
        contextMenuRef.current = data;
        _setContextMenuState(data);
    };

    const moveToEnabled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.moveToEnabled);

    const [commandBarMenuState, _setCommandBarMenuState] = React.useState<ICommandBarMenuProps>();

    const commandBarMenuRef = React.useRef(commandBarMenuState);
    const setCommandBarMenu = (data: ICommandBarMenuProps) => {
        commandBarMenuRef.current = data;
        _setCommandBarMenuState(data);
    };

    React.useEffect(() => {
        const menu: IContextualMenuItem[] = [];
        const commandbarMenu: ICommandBarMenuProps = {
            items: [],
            farItems: []
        }

        moveToEnabled ? moveToEnabledCommandBar(menu, commandbarMenu) : moveToDisabledCommandBar(menu, commandbarMenu);

        setContextMenu(menu);
        setCommandBarMenu(commandbarMenu);
    }, [
        mode,
        moveToEnabled,
        selectedItems,
        menuItem_Automation,
        menuItem_Checkin,
        menuItem_Checkout,
        menuItem_ClearSearch,
        menuItem_ClearFilter,
        menuItem_CopyTo,
        menuItem_CurrentLocation,
        menuItem_Delete,
        menuItem_CancelAssignment,
        menuItem_DiscardCheckout,
        menuItem_Download,
        menuItem_DownloadAsPDF,
        menuItem_Error,
        menuItem_Filter,
        menuItem_LoadingFiles,
        menuItem_MoveTo,
        menuItem_MoveToCancel,
        menuItem_More,
        menuItem_New,
        menuItem_Open,
        menuItem_PII,
        menuItem_Pin,
        menuItem_Preview,
        menuItem_Preview_IconOnly,
        menuItem_Properties,
        menuItem_Properties_IconOnly,
        menuItem_Publish,
        menuItem_QuickFilter,
        menuItem_Refresh,
        menuItem_Rename,
        menuItem_Replace,
        menuItem_SavingFiles,
        menuItem_SelectedFileCount,
        menuItem_SelectedMoveFileCount,
        menuItem_Share,
        menuItem_SigningRequest,
        menuItem_Unpublish,
        menuItem_Upload,
        menuItem_VersionAppRej,
        menuItem_VersionHistory,
        menuItem_Views,
        menuItem_ShareToTeams
    ]);

    const moveToDisabledCommandBar = (menu: IContextualMenuItem[], commandbarMenu: ICommandBarMenuProps) => {

        if (mode === ModeEnum.contentManagerTeams && menuItem_ShareToTeams) {
            menu.push(menuItem_ShareToTeams);
        }

        if (menuItem_New) {
            commandbarMenu.items.push(menuItem_New);
        }

        if (menuItem_Upload) {
            commandbarMenu.items.push(menuItem_Upload);
        }

        if (menuItem_NewDatasourceDocumentTemplate) {
            commandbarMenu.items.push(menuItem_NewDatasourceDocumentTemplate);
        }

        if (menuItem_CurrentLocation) {
            menu.push(menuItem_CurrentLocation);
            commandbarMenu.items.push(menuItem_CurrentLocation);
        }

        if (menuItem_Open) {
            menu.push(menuItem_Open);
            commandbarMenu.items.push(menuItem_Open);
        }

        if (menuItem_Preview) {
            menu.push(menuItem_Preview);
        }

        if (menuItem_Share) {
            menu.push(menuItem_Share);
            commandbarMenu.items.push(menuItem_Share);
        }

        if (menuItem_CopyLink) {
            menu.push(menuItem_CopyLink);
            commandbarMenu.items.push(menuItem_CopyLink);
        }

        if (menuItem_Rename) {
            menu.push(menuItem_Rename);
            commandbarMenu.items.push(menuItem_Rename);
        }

        if (menuItem_Replace) {
            menu.push(menuItem_Replace);
            commandbarMenu.items.push(menuItem_Replace);
        }

        if (menuItem_Download) {
            menu.push(menuItem_Download);
            commandbarMenu.items.push(menuItem_Download);
        }

        if (menuItem_CopyTo) {
            menu.push(menuItem_CopyTo);
            commandbarMenu.items.push(menuItem_CopyTo);
        }

        if (menuItem_MoveTo) {
            menu.push(menuItem_MoveTo);
            commandbarMenu.items.push(menuItem_MoveTo);
        }

        if (menuItem_DownloadAsPDF) {
            menu.push(menuItem_DownloadAsPDF);
            commandbarMenu.items.push(menuItem_DownloadAsPDF);
        }

        if (menuItem_SaveAsPDF) {
            menu.push(menuItem_SaveAsPDF);
            commandbarMenu.items.push(menuItem_SaveAsPDF);
        }

        if (menuItem_Delete) {
            menu.push(menuItem_Delete);
            commandbarMenu.items.push(menuItem_Delete);
        }

        if (menuItem_CancelAssignment) {
            menu.push(menuItem_CancelAssignment);
            commandbarMenu.items.push(menuItem_CancelAssignment);
        }

        if (menuItem_PII) {
            menu.push(menuItem_PII);
            commandbarMenu.items.push(menuItem_PII);
        }

        if (menuItem_Automation) {
            menu.push(menuItem_Automation);
            commandbarMenu.items.push(menuItem_Automation);
        }

        if (menuItem_SigningRequest) {
            menu.push(menuItem_SigningRequest);
            commandbarMenu.items.push(menuItem_SigningRequest);
        }

        if (menuItem_Pin) {
            menu.push(menuItem_Pin);
            commandbarMenu.items.push(menuItem_Pin);
        }

        if (menuItem_VersionHistory) {
            menu.push(menuItem_VersionHistory);
        }

        if (menuItem_More) {
            menu.push(menuItem_More);
        }

        if (menuItem_Properties) {
            menu.push(menuItem_Properties);
        }

        if (menuItem_VersionHistory) {
            commandbarMenu.items.push(menuItem_VersionHistory);
        }

        if (menuItem_Checkout) {
            commandbarMenu.items.push(menuItem_Checkout);
        }

        if (menuItem_Checkin) {
            commandbarMenu.items.push(menuItem_Checkin);
        }

        if (menuItem_DiscardCheckout) {
            commandbarMenu.items.push(menuItem_DiscardCheckout);
        }

        if (menuItem_Publish) {
            commandbarMenu.items.push(menuItem_Publish);
        }

        if (menuItem_Unpublish) {
            commandbarMenu.items.push(menuItem_Unpublish);
        }

        if (menuItem_VersionAppRej) {
            commandbarMenu.items.push(menuItem_VersionAppRej);
        }

        if (menuItem_QuickFilter) {
            commandbarMenu.items.push(menuItem_QuickFilter);
        }

        //far items
        if (menuItem_SelectedFileCount) {
            commandbarMenu.farItems.push(menuItem_SelectedFileCount);
        }

        if (menuItem_ClearSearch) {
            commandbarMenu.farItems.push(menuItem_ClearSearch);
        }

        if (menuItem_ClearFilter) {
            commandbarMenu.farItems.push(menuItem_ClearFilter);
        }

        if (menuItem_Error) {
            commandbarMenu.farItems.push(menuItem_Error);
        }

        if (menuItem_SavingFiles) {
            commandbarMenu.farItems.push(menuItem_SavingFiles);
        }

        if (menuItem_SelectedMoveFileCount) {
            commandbarMenu.farItems.push(menuItem_SelectedMoveFileCount);
        }

        if (menuItem_LoadingFiles) {
            commandbarMenu.farItems.push(menuItem_LoadingFiles);
        }

        if (menuItem_Properties_IconOnly) {
            commandbarMenu.farItems.push(menuItem_Properties_IconOnly);
        }

        if (menuItem_Preview_IconOnly) {
            commandbarMenu.farItems.push(menuItem_Preview_IconOnly);
        }

        if (menuItem_Views) {
            commandbarMenu.farItems.push(menuItem_Views);
        }

        if (menuItem_Filter) {
            commandbarMenu.farItems.push(menuItem_Filter);
        }

        if (menuItem_Refresh) {
            commandbarMenu.farItems.push(menuItem_Refresh);
        }
    }

    const moveToEnabledCommandBar = (menu: IContextualMenuItem[], commandbarMenu: ICommandBarMenuProps) => {
        if (menuItem_CopyTo) {
            menu.push(menuItem_CopyTo);
            commandbarMenu.items.push(menuItem_CopyTo);
        }

        if (menuItem_MoveTo) {
            menu.push(menuItem_MoveTo);
            commandbarMenu.items.push(menuItem_MoveTo);
        }

        if (menuItem_MoveToCancel) {
            menu.push(menuItem_MoveToCancel);
            commandbarMenu.items.push(menuItem_MoveToCancel);
        }

        //far items
        if (menuItem_SelectedMoveFileCount) {
            commandbarMenu.farItems.push(menuItem_SelectedMoveFileCount);
        }
    }

    const contextMenu = contextMenuRef.current;
    const commandBarMenu = commandBarMenuRef.current;

    return {
        contextMenu,
        commandBarMenu,
        deleteSuccess
    }
}